/**
 * „Vor Ort bar bezahlt" — ein Projekt ohne Rechnung abschließen (Benjamin, 07.10.2026).
 *
 * Fall Fischer: AG0206 angenommen, vor Ort verkleinert, der kleinere Betrag bar
 * bezahlt. Der Server (kontor_barzahlung) speichert die Zahlung, rechnet den
 * Auftragswert daraus, trägt offene Angebote als angenommen ein und schließt
 * das Projekt ab. Danach ist nichts mehr „noch nicht abgerechnet".
 *
 * Eingegeben wird, was in der Hand lag — brutto. Der Nettowert daneben ist der
 * neue Projektwert.
 *
 * Wer das Geld genommen hat, ist Pflicht (Benjamin, 20.10.2026: „Das muss dann
 * als Privatentnahme gezählt werden."): der Server bucht den Bruttobetrag als
 * Entnahme auf dessen Gesellschafterkonto und zieht sie bei jeder Änderung mit.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { fehlerText } from "@vimaana/ui";

import { useBarzahlungEntfernen, useBarzahlungSpeichern } from "../../hooks/useBarzahlung";
import { useTeamOwners } from "../../hooks/usePlanung";
import type { InvoiceRegisterItem } from "../../lib/invoices";
import type { ProjektLage } from "../../lib/projektLage";
import type { Barzahlung } from "../../lib/types";

const eur = (n: number) => n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const zahl = (text: string) => Number(text.replace(/\./g, "").replace(",", "."));
const heute = () => new Date().toISOString().slice(0, 10);

type Props = {
  projektId: string;
  lage: ProjektLage;
  angebote: InvoiceRegisterItem[];
  bar: Barzahlung | null | undefined;
  onClose: () => void;
  onFertig: (text: string) => void;
};

export default function BarBezahltDialog({ projektId, lage, angebote, bar, onClose, onFertig }: Props) {
  const speichern = useBarzahlungSpeichern(projektId);
  const entfernen = useBarzahlungEntfernen(projektId);
  const { data: owners = [] } = useTeamOwners();
  /* Vorschlag: was noch nicht abgerechnet ist, mit 19 % — bei Fischer der von Hand gesenkte Projektwert. */
  const rest = Math.max(0, lage.geld.nicht_abgerechnet, lage.geld.auftragswert - lage.geld.gestellt);
  const [betrag, setBetrag] = useState(
    bar ? String(bar.brutto).replace(".", ",") : rest > 0 ? (Math.round(rest * 119) / 100).toFixed(2).replace(".", ",") : "",
  );
  const [ust, setUst] = useState(bar ? bar.ust_satz : 19);
  const [datum, setDatum] = useState(bar?.zahlungsdatum || lage.termine.letzter || heute());
  const [owner, setOwner] = useState(bar?.empfaenger_owner_id || "");
  const [vermerk, setVermerk] = useState(bar?.vermerk || "");

  const brutto = zahl(betrag);
  const gueltig = brutto > 0 && Boolean(datum) && Boolean(owner);
  const ownerName = owners.find((o) => o.id === owner)?.name || "";
  const netto = gueltig ? Math.round((brutto / (1 + ust / 100)) * 100) / 100 : 0;
  const offen = angebote.filter((a) => a.status === "gestellt");
  const hoeher = angebote.filter((a) => a.status === "angenommen" && a.netto > netto + 0.005);
  const laeuft = speichern.isPending || entfernen.isPending;
  const fehler = speichern.error || entfernen.error;

  const sichern = () => speichern.mutate(
    { brutto, zahlungsdatum: datum, ust_satz: ust, empfaenger_owner_id: owner, vermerk: vermerk.trim() },
    {
      onSuccess: (a) => {
        const neu = a.angebote_angenommen.length ? ` ${a.angebote_angenommen.join(", ")} als angenommen eingetragen.` : "";
        onFertig(`Bar bezahlt gebucht — Projektwert ${eur(a.auftragswert)} netto, abgeschlossen, ${eur(brutto)} als Privatentnahme von ${ownerName}.${neu}`);
      },
    },
  );
  const zuruecknehmen = () => {
    if (!window.confirm("Barzahlung entfernen? Der Projektwert rechnet dann wieder aus Angebot und Rechnungen.")) return;
    entfernen.mutate(undefined, { onSuccess: () => onFertig("Barzahlung entfernt.") });
  };

  return createPortal(
    <div className="vg-overlay" role="dialog" aria-modal="true" aria-label="Vor Ort bar bezahlt">
      <button type="button" className="vg-overlay-backdrop" aria-label="Schließen" onClick={onClose} />
      <div className="vg-overlay-panel">
        <div className="vg-overlay-head">
          <h2 className="vg-overlay-title">Vor Ort bar bezahlt</h2>
          <button type="button" className="vg-overlay-close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <p className="pk-meta bb-intro">
          Das Projekt wird ohne Rechnung abgeschlossen. Der Betrag gilt als eingegangen, Angebote sind danach nicht mehr offen.
        </p>
        <div className="vg-form-grid">
          <label>
            Bar erhalten (€)
            <input autoFocus inputMode="decimal" value={betrag} onChange={(e) => setBetrag(e.target.value)} />
          </label>
          <label>
            Umsatzsteuer
            <select value={ust} onChange={(e) => setUst(Number(e.target.value))}>
              <option value={19}>inkl. 19 %</option>
              <option value={7}>inkl. 7 %</option>
              <option value={0}>ohne</option>
            </select>
          </label>
          <label>
            Bezahlt am
            <input type="date" value={datum} max={heute()} onChange={(e) => setDatum(e.target.value)} />
          </label>
          <label>
            Geld genommen hat
            <select value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="" disabled>— bitte wählen —</option>
              {owners.filter((o) => o.active || o.id === owner).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          <label className="is-wide">
            Vermerk
            <input value={vermerk} placeholder="z. B. vor Ort reduziert: ohne Hecke hinten" onChange={(e) => setVermerk(e.target.value)} />
          </label>
        </div>
        <ul className="bb-folgen">
          {gueltig && <li><strong>{eur(netto)} netto</strong> wird der Projektwert{ust > 0 ? ` (${eur(brutto)} brutto)` : ""}.</li>}
          {hoeher.map((a) => (
            <li key={a.id}>Angebot {a.nummer} über {eur(a.netto)} netto gilt damit als erledigt.</li>
          ))}
          {offen.length > 0 && <li>{offen.map((a) => a.nummer).join(", ")} {offen.length === 1 ? "wird" : "werden"} als angenommen eingetragen.</li>}
          {lage.geld.offen > 0.005 && <li>Gestellte Rechnungen über {eur(lage.geld.offen)} netto bleiben offen.</li>}
          {lage.termine.kuenftig > 0 && <li>{lage.termine.kuenftig === 1 ? "Ein geplanter Termin bleibt" : `${lage.termine.kuenftig} geplante Termine bleiben`} im Kalender.</li>}
          {brutto > 0 && ownerName && <li>Als Privatentnahme von {ownerName} gebucht: <strong>{eur(brutto)}</strong>.</li>}
          {!owner && <li>Bitte angeben, wer das Geld genommen hat — es zählt als Privatentnahme.</li>}
          <li>An DATEV geht dafür kein Beleg.</li>
        </ul>
        {fehler && <div className="vg-banner is-warn">{fehlerText(fehler, "Die Barzahlung konnte nicht gespeichert werden.")}</div>}
        <div className="vg-overlay-actions">
          {bar && (
            <button type="button" className="vg-btn vg-btn-ghost bb-entfernen" disabled={laeuft} onClick={zuruecknehmen}>
              Barzahlung entfernen
            </button>
          )}
          <button type="button" className="vg-btn vg-btn-ghost" onClick={onClose}>Abbrechen</button>
          <button type="button" className="vg-btn vg-btn-primary" disabled={!gueltig || laeuft} onClick={sichern}>
            {speichern.isPending ? "Speichert …" : bar ? "Speichern" : "Bar bezahlt · abschließen"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
